/**
 * Money received.
 *
 * A read-only register. A payment is recorded against the invoice it settles —
 * that is the only place the arithmetic works out, because recording one here
 * would mean choosing an invoice from a list and hoping the amount fits.
 *
 * The invoice code links back, which is the question this page is usually open
 * to answer: "what was this 61,950 for".
 */

import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Building2, CalendarDays, Wallet } from 'lucide-react';
import {
  PageHeader,
  ServerTable,
  api,
  createFacetsFetcher,
  createServerFetcher,
  formatDate,
  formatMoney,
  type ServerColumn,
} from '@connect/web-shared';

import { PAYMENT_METHOD_LABELS } from '../../lib/tones';

export interface PaymentRow {
  public_id: string;
  code: string;
  received_on: string;
  amount: string;
  currency: string;
  method: string;
  reference: string | null;
  notes: string | null;
  client_name: string;
  invoice_code: string;
  invoice_public_id: string;
  created_at: string;
}

interface FinanceOptions {
  methods: { value: string; label: string }[];
}

const ENDPOINT = '/finance/payments';

export function PaymentsPage() {
  const navigate = useNavigate();

  const options = useQuery({
    queryKey: ['finance', 'options'],
    queryFn: () => api.get<FinanceOptions>('/finance/options'),
    staleTime: 10 * 60_000,
  });

  const fetchRows = useMemo(() => createServerFetcher<PaymentRow>(ENDPOINT), []);
  const getFacets = useMemo(() => createFacetsFetcher(ENDPOINT), []);

  const columns = useMemo<ServerColumn<PaymentRow>[]>(
    () => [
      {
        field: 'code',
        header: 'Receipt',
        filter: 'text',
        width: 130,
        render: (row) => <span className="font-mono text-xs text-muted">{row.code}</span>,
      },
      {
        field: 'received_on',
        header: 'Received',
        icon: <CalendarDays className="size-3" />,
        filter: 'date',
        width: 130,
        format: (value) => formatDate(value as string),
      },
      {
        field: 'client_name',
        header: 'Client',
        icon: <Building2 className="size-3" />,
        filter: 'select',
        minWidth: 190,
      },
      {
        field: 'invoice_code',
        header: 'Against',
        filter: 'text',
        width: 140,
        render: (row) => (
          <button
            type="button"
            className="font-mono text-xs text-accent hover:underline"
            onClick={(event) => {
              // Stops the row's own click handler opening the detail panel
              // behind the navigation.
              event.stopPropagation();
              navigate(`/invoices/${row.invoice_public_id}`);
            }}
          >
            {row.invoice_code}
          </button>
        ),
      },
      {
        field: 'amount',
        header: 'Amount',
        icon: <Wallet className="size-3" />,
        filter: 'number',
        numeric: true,
        width: 140,
        format: (value, row) => formatMoney(value as string, row.currency),
      },
      {
        field: 'method',
        header: 'How',
        filter: 'select',
        options: options.data?.methods,
        width: 140,
        format: (value) => PAYMENT_METHOD_LABELS[value as string] ?? String(value),
      },
      { field: 'reference', header: 'Reference', filter: 'text', minWidth: 170 },
      { field: 'notes', header: 'Notes', filter: 'text', minWidth: 200, hiddenByDefault: true },
    ],
    [options.data, navigate],
  );

  return (
    <div className="space-y-4">
      <PageHeader
        title="Payments"
        description="Every receipt, and the invoice it settled."
      />

      <ServerTable<PaymentRow>
        columns={columns}
        fetchRows={fetchRows}
        getFacets={getFacets}
        rowKey={(row) => row.public_id}
        queryKey={['finance', 'payments']}
        stateKey="finance.payments"
        searchPlaceholder="Search by client, reference or invoice"
        rowDetailFields={(row) => [
          { label: 'Client', value: row.client_name },
          { label: 'Invoice', value: row.invoice_code },
          { label: 'Amount', value: formatMoney(row.amount, row.currency) },
          { label: 'Method', value: PAYMENT_METHOD_LABELS[row.method] ?? row.method },
          { label: 'Reference', value: row.reference },
          { label: 'Notes', value: row.notes },
        ]}
        emptyTitle="Nothing received yet"
        emptyDescription="A payment is recorded on the invoice it settles — open an issued invoice and record it there."
      />
    </div>
  );
}
